// Story 7.8 — query hook for GET /api/v1/creators/me/aliases (the
// creator's linked external accounts; Twitch at v1).

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export const LINKED_ACCOUNTS_QUERY_KEY = ["creator", "linked-accounts"] as const;

export interface LinkedAccountAlias {
  source_kind: string;
  alias: string;
  verified_at: string | null;
}

export interface LinkedAccountsResponse {
  data: LinkedAccountAlias[];
  /** Whether the Twitch OAuth integration is configured on this
   *  deployment. When false the SPA hides the Twitch link affordance so
   *  users don't hit a BAAS_PROVIDER_ERROR from an unconfigured env. */
  twitch_link_enabled: boolean;
  /** Story 7.20 — whether the YouTube linkage is available TO THIS
   *  CALLER. Per-user, not per-deployment: the Google app is unverified
   *  during the beta, so the flow is credential-configured AND
   *  allowlisted. Optional so pre-7.20 fixtures still typecheck. */
  youtube_link_enabled?: boolean;
  /** Story 7.21 — whether the Kick OAuth linkage is configured on this
   *  deployment. Per-deployment like Twitch: Kick has no app review, so
   *  there is no per-user containment to express. Optional so pre-7.21
   *  fixtures still typecheck. */
  kick_link_enabled?: boolean;
}

async function fetchLinkedAccounts(): Promise<LinkedAccountsResponse> {
  const response = await fetch("/api/v1/creators/me/aliases", {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw new Error(`linked accounts fetch failed (HTTP ${response.status})`);
  }
  return (await response.json()) as LinkedAccountsResponse;
}

export function useLinkedAccounts(): UseQueryResult<LinkedAccountsResponse, Error> {
  return useQuery({
    queryKey: LINKED_ACCOUNTS_QUERY_KEY,
    queryFn: fetchLinkedAccounts,
    staleTime: 30_000,
    retry: false,
  });
}
